import {
  Spin,
  Timeline,
  Card,
  Row,
  Button,
  Drawer,
  Input,
  Alert,
  Switch,
  Space,
  Typography,
  Divider,
  Badge,
  Result,
} from "antd";
import {
  ShoppingOutlined,
  ShopOutlined,
  BarChartOutlined,
  TruckOutlined,
  DeleteOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
} from "@ant-design/icons";
import { useHistoricalData } from "hooks/historicalData/useHistoricalData";
import { useEffect, useMemo, useState } from "react";
import ResponsiveCol from "common/components/form/ResponsiveCol";

interface MigrationStepProps {
  executeMigration: (kill: boolean) => void;
  validationLogs: string;
  cleanupLogs: string;
  insertLogs: string;
  loading: boolean;
  wasExecuted: boolean;
}

interface EntityCount {
  name: string;
  count: number;
  icon: React.ReactNode;
  color: string;
}

function MigrationStep({
  executeMigration,
  cleanupLogs,
  validationLogs,
  insertLogs,
  loading,
  wasExecuted,
}: MigrationStepProps) {
  const { processValidationLogs } = useHistoricalData();
  const [showTechnicalData, setShowTechnicalData] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);

  const [confirmationText, setConfirmationText] = useState("");
  const [entityCounts, setEntityCounts] = useState<EntityCount[]>([]);

  // Revisar si ya se ejecuto el proceso de validacion
  const existSummary: boolean = useMemo(() => {
    return validationLogs.includes("✅ Resumen guardado en");
  }, [validationLogs]);

  useEffect(() => {
    if (cleanupLogs.length === 0 && existSummary) {
      executeMigration(false);
    }
  }, [cleanupLogs, existSummary]);

  // Función para extraer el número de registros de cada línea
  const getCount = (line?: string): number => {
    if (!line) return 0;

    const countMatch = line.match(/Se eliminarían (\d+) filas/);
    if (countMatch && countMatch[1]) {
      return parseInt(countMatch[1], 10);
    }

    return 0;
  };

  useEffect(() => {
    const result = processValidationLogs(cleanupLogs);

    const traspasosLine = result.find((line) =>
      line.children.includes("[traspasos]")
    )?.children;
    const ventasLine = result.find((line) =>
      line.children.includes("[ventas]")
    )?.children;
    const inventariosLine = result.find((line) =>
      line.children.includes("[inventarios]")
    )?.children;
    const comprasLine = result.find((line) =>
      line.children.includes("[compras]")
    )?.children;

    const PRIMARY_COLOR = "#33AADD";
    const WARNING_COLOR = "#c28711ff";

    const counts: EntityCount[] = [
      {
        name: "Traspasos",
        count: getCount(traspasosLine),
        icon: <TruckOutlined />,
        color: getCount(traspasosLine) > 0 ? WARNING_COLOR : PRIMARY_COLOR,
      },
      {
        name: "Ventas",
        count: getCount(ventasLine),
        icon: <ShopOutlined />,
        color: getCount(ventasLine) > 0 ? WARNING_COLOR : PRIMARY_COLOR,
      },
      {
        name: "Inventarios",
        count: getCount(inventariosLine),
        icon: <BarChartOutlined />,
        color: getCount(inventariosLine) > 0 ? WARNING_COLOR : PRIMARY_COLOR,
      },
      {
        name: "Compras",
        count: getCount(comprasLine),
        icon: <ShoppingOutlined />,
        color: getCount(comprasLine) > 0 ? WARNING_COLOR : PRIMARY_COLOR,
      },
    ];

    setEntityCounts(counts);
  }, [cleanupLogs]);

  const migrationLogs = useMemo(() => {
    return processValidationLogs(cleanupLogs);
  }, [cleanupLogs]);

  const insLogs = useMemo(() => {
    return processValidationLogs(insertLogs);
  }, [insertLogs]);

  const totalToDelete = useMemo(() => {
    return entityCounts.reduce((total, entity) => total + entity.count, 0);
  }, [entityCounts]);

  const handleConfirmMigration = () => {
    if (confirmationText === "ELIMINAR") {
      setDrawerVisible(false);
      setConfirmationText("");
      executeMigration(true);
    }
  };

  return (
    <Spin spinning={loading} tip="Este proceso puede durar bastante tiempo">
      <Divider />
      <Row>
        <Space
          direction="vertical"
          style={{ width: "100%", textAlign: "right" }}
        >
          <Switch
            checked={showTechnicalData}
            onChange={setShowTechnicalData}
            checkedChildren={<EyeOutlined />}
            unCheckedChildren={<EyeInvisibleOutlined />}
            style={{}}
          />
          <Typography.Text>
            Mostrar datos técnicos de la eliminación
          </Typography.Text>
        </Space>
      </Row>
      <Row>
        <Alert
          message="REVISA LO SIGUIENTE CON ATENCIÓN"
          description={
            <div>
              <p>
                Al aceptar, confirmas que la información operativa existente
                será eliminada, y reemplazada por la que acabas de subir en los
                layouts. Esta acción es definitiva, y no se puede volver a un
                estado anterior una vez aceptes la migración.
              </p>

              {showTechnicalData && (
                <p>
                  <strong>
                    Total de registros a eliminar:{" "}
                    {totalToDelete.toLocaleString()}
                  </strong>
                </p>
              )}
            </div>
          }
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      </Row>

      <Row gutter={24}>
        {entityCounts.map((entity, index) => {
          let value = entity.count > 0 ? "Existen" : "Sin";
          if (showTechnicalData) {
            value = `${entity.count}`;
          }
          return (
            <ResponsiveCol size="xs" key={index}>
              <Badge.Ribbon color={entity.color} text={entity.icon}>
                <Card title={entity.name}>
                  <span style={{ color: entity.color }}>
                    {value} registros por eliminar
                  </span>
                </Card>
              </Badge.Ribbon>
            </ResponsiveCol>
          );
        })}
      </Row>

      <Row>
        {showTechnicalData && (
          <>
            <Divider />
            <Timeline items={migrationLogs} />
          </>
        )}
      </Row>

      <Row>
        {insLogs.length > 0 && showTechnicalData && (
          <div>
            <Divider />
            <Divider>Resultado de la Inserción </Divider>
            <Timeline items={insLogs} />
          </div>
        )}
      </Row>

      <Row>
        {wasExecuted && (
          <>
            <Divider />
            <Result
              style={{ width: "100%" }}
              status="success"
              title="El Proceso de Migración se ha completado exitosamente"
              subTitle="Los cambios fueron aplicados correctamente en el sistema."
            />
          </>
        )}
      </Row>

      <Divider />
      <Space direction="vertical" style={{ width: "100%" }}>
        <Button
          type="primary"
          danger
          icon={<DeleteOutlined />}
          size="large"
          onClick={() => setDrawerVisible(true)}
          style={{ width: "100%" }}
        >
          Eliminar y Migrar
        </Button>
      </Space>

      <Drawer
        title="Confirmar Eliminación y Migración"
        placement="right"
        onClose={() => {
          setDrawerVisible(false);
          setConfirmationText("");
        }}
        open={drawerVisible}
        width={500}
        footer={
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button
              onClick={() => {
                setDrawerVisible(false);
                setConfirmationText("");
              }}
            >
              Cancelar
            </Button>
            <Button
              type="primary"
              danger
              onClick={handleConfirmMigration}
              disabled={confirmationText !== "ELIMINAR"}
            >
              Confirmar Migración
            </Button>
          </Space>
        }
      >
        <Alert
          message="PROCESO DE SOBREESCRITURA DE DATOS"
          description={
            <div>
              <p>
                Se eliminarán los datos operativos actuales y se migrarán los
                nuevos datos desde los archivos que has cargado. Esta acción
                reemplazará completamente la información existente.
              </p>
              <p>
                <strong>Esta operación NO se puede deshacer.</strong>
              </p>
            </div>
          }
          type="error"
          showIcon
        />
        <Divider />

        {showTechnicalData && (
          <div>
            <Typography.Title level={5}>
              Resumen de eliminación:
            </Typography.Title>
            {entityCounts.map((entity, index) => (
              <p key={index}>
                <span style={{ color: entity.color, marginRight: 8 }}>
                  {entity.icon}
                </span>
                {entity.name}: {entity.count.toLocaleString()} registros
              </p>
            ))}
            <p>
              <strong>Total: {totalToDelete.toLocaleString()} registros</strong>
            </p>
            <Divider />
          </div>
        )}

        <div>
          <p>
            Esta acción eliminará la información operativa existente y migrará
            los nuevos datos desde los layouts que has subido previamente.
          </p>
          <p>
            El proceso de migración puede durar un tiempo en completarse,
            dependiendo de la cantidad de información a registrar, por favor, se
            paciente
          </p>
          <p>
            Para confirmar que deseas proceder con la sobreescritura de datos,
            escribe <strong>ELIMINAR</strong> en el campo de abajo:
          </p>
          <Divider />
          <Input
            placeholder="Escribe ELIMINAR para confirmar"
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value.toUpperCase())}
          />
        </div>
      </Drawer>
    </Spin>
  );
}

export default MigrationStep;
